import { fmtDateTime } from '@/lib/dates.js';
import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Info, Loader2, Plus, Search, Users, X } from 'lucide-react';
import { subjects, studies, compliance, type SubjectStatus } from '@/api/client';
import { encryptId } from '@/lib/encryptId';
import { RoleGate } from '@/components/RoleGate';
import { CAN_CREATE_SUBJECT } from '@/lib/permissions';
import {
  SUBJECT_STATUSES,
  humanSubjectStatus,
  subjectStatusBadgeClass,
} from '@/lib/subjectStatus';
import { SingleSelectSearch, type SingleSelectOption } from '@/components/ui/SingleSelectSearch';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { studyNameById } from '@/lib/studyLabel';
import { studyLabel } from '@/lib/studyLabel';
import { subjectDisplayId } from '@/lib/subjectLabel';

// The filter offers every stored status, in lifecycle order, from the one
// vocabulary — so a status added to it becomes filterable here for free.
const STATUS_OPTIONS: MultiSelectOption[] = SUBJECT_STATUSES.map((s) => ({
  value: s,
  label: humanSubjectStatus(s),
}));

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export function SubjectsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  const prescopeStudyId = searchParams.get('study_id') ?? undefined;

  // ── Filter state ──────────────────────────────────────────────────────
  const [filterStudyIds, setFilterStudyIds] = useState<string[]>(
    prescopeStudyId ? [prescopeStudyId] : [],
  );
  const [filterStatuses, setFilterStatuses] = useState<SubjectStatus[]>([]);
  const [filterReConsent, setFilterReConsent] = useState<'all' | 'yes' | 'no'>('all');

  // ── Pagination state ──────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (prescopeStudyId) setFilterStudyIds([prescopeStudyId]);
  }, [prescopeStudyId]);

  // Reset to page 1 whenever any filter changes.
  useEffect(() => {
    setPage(1);
  }, [filterStudyIds, filterStatuses, filterReConsent]);

  // ── Modal state ───────────────────────────────────────────────────────
  // Phase 2 Defect 2.4 — subject external_ref is server-minted as SUB-####;
  // no user input required. The old `external_ref` and `extRefError` state
  // fields have been removed.
  const [creating, setCreating] = useState(!!prescopeStudyId);
  const [draft, setDraft] = useState({
    study_id: prescopeStudyId ?? '',
    full_name: '',
  });

  function closeModal() {
    setCreating(false);
    setDraft({ study_id: '', full_name: '' });
  }

  // ── Studies — for filter dropdown + modal ─────────────────────────────
  const studiesQuery = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });

  const studyFilterOptions = useMemo<MultiSelectOption[]>(
    () =>
      (studiesQuery.data?.rows ?? []).map((s) => ({
        value: s.study_id,
        label: studyLabel(s),
        sublabel: s.sponsor,
      })),
    [studiesQuery.data],
  );

  const studyById = useMemo(
    () => new Map((studiesQuery.data?.rows ?? []).map((s) => [s.study_id, s])),
    [studiesQuery.data],
  );

  const studyModalOptions = useMemo<SingleSelectOption[]>(
    () =>
      (studiesQuery.data?.rows ?? [])
        .filter((s) => s.status === 'active')
        .map((s) => ({
          value: s.study_id,
          label: studyLabel(s),
          sublabel: s.sponsor,
        })),
    [studiesQuery.data],
  );

  useEffect(() => {
    if (!prescopeStudyId || !studiesQuery.data) return;
    const match = studiesQuery.data.rows.find((s) => s.study_id === prescopeStudyId);
    if (match)
      setDraft((d) => (d.study_id === match.study_id ? d : { ...d, study_id: match.study_id }));
  }, [prescopeStudyId, studiesQuery.data]);

  // ── Subjects list — server-side filtered + paginated ──────────────────
  const studyIdParam = filterStudyIds.length > 0 ? filterStudyIds.join(',') : undefined;
  const statusParam = filterStatuses.length > 0 ? filterStatuses.join(',') : undefined;
  const reConsentParam = filterReConsent !== 'all' ? filterReConsent : undefined;

  const list = useQuery({
    queryKey: ['subjects', studyIdParam, statusParam, reConsentParam, page, pageSize],
    queryFn: () =>
      subjects.list({
        study_id: studyIdParam,
        status: statusParam,
        re_consent: reConsentParam,
        page,
        pageSize,
      }),
    staleTime: 0,
  });

  const rows = list.data?.rows ?? [];
  const hasMore = list.data?.has_more ?? false;
  const total = list.data?.total ?? null;

  // Phase 4 extended contract (AC-EXT-10) — per-subject open-query
  // counts. Only queried when a single study is filtered — the BE
  // endpoint is per-study; showing "—" when the directory spans
  // multiple studies keeps the column honest without a fan-out.
  const openCountsQ = useQuery({
    queryKey: ['study-subject-open-query-counts', studyIdParam],
    queryFn: () => compliance.studySubjectOpenQueryCounts(studyIdParam!),
    enabled: !!studyIdParam,
    staleTime: 30_000,
  });
  const openCountsBySubject = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of openCountsQ.data?.counts ?? []) {
      map.set(row.subject_id, row.open_count);
    }
    return map;
  }, [openCountsQ.data]);

  function rowHref(row: { subject_id: string; status: SubjectStatus }): string {
    const enc = encryptId(row.subject_id);
    // Mid-onboarding subjects open the wizard; everyone else opens the record.
    // 'screening' alone now, since consent no longer advances the status —
    // a consented-but-not-enrolled subject is still 'screening'. 'screen_fail'
    // is NOT here: there is no wizard left to resume.
    return row.status === 'screening'
      ? `/subjects/onboarding?subject_id=${enc}`
      : `/subjects/${enc}`;
  }

  // ── Create subject ─────────────────────────────────────────────────────
  // Duplicate-check query removed with Phase 2 Defect 2.4 — the server
  // now mints SUB-#### atomically, so client-side dupe pre-check is not
  // needed. Belt-and-braces uniqueness check remains on the server.

  const create = useMutation({
    mutationFn: () =>
      subjects.create({
        study_id: draft.study_id.trim(),
        // external_ref is server-minted (SUB-####); no client-supplied value.
        status: 'screening',
        ...(draft.full_name ? { phi: { full_name: draft.full_name.trim() } } : {}),
      }),
    onSuccess: (data) => {
      // No Subject ID yet (screening records it), so REF-SUB-xxxx.
      toast.success(`Subject ${subjectDisplayId(data)} created`);
      qc.invalidateQueries({ queryKey: ['subjects'] });
      navigate(`/subjects/onboarding?subject_id=${encryptId(data.subject_id)}`);
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to create subject')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draft.study_id.trim()) return;
    create.mutate();
  }

  // Page info helpers
  const from = (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + rows.length;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: 'Subjects' }]}
        title="Subjects"
        subtitle="Enrolled participants — PHI is HIPAA Safe Harbor redacted by default."
        actions={
          <RoleGate roles={CAN_CREATE_SUBJECT}>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New subject
            </button>
          </RoleGate>
        }
      />

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-end gap-3 border-b border-surface-border p-3">
          <div className="w-full max-w-[240px]">
            <label className="label text-xs">Study</label>
            <MultiSelectSearch
              options={studyFilterOptions}
              value={filterStudyIds}
              onChange={setFilterStudyIds}
              placeholder="All studies"
            />
          </div>
          <div className="w-full max-w-[200px]">
            <label className="label text-xs">Status</label>
            <MultiSelectSearch
              options={STATUS_OPTIONS}
              value={filterStatuses}
              onChange={(v) => setFilterStatuses(v as SubjectStatus[])}
              placeholder="Any status"
            />
          </div>
          <div className="w-full max-w-[180px]">
            <label className="label text-xs">Re-consent</label>
            <select
              className="input h-10 text-sm"
              value={filterReConsent}
              onChange={(e) => setFilterReConsent(e.target.value as 'all' | 'yes' | 'no')}
            >
              <option value="all">All</option>
              <option value="yes">Required</option>
              <option value="no">Not required</option>
            </select>
          </div>
          <div className="ml-auto">
            <button
              type="button"
              className="btn-secondary"
              disabled={list.isFetching}
              onClick={() => list.refetch()}
            >
              {list.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {list.isFetching ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {list.isFetching ? (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : list.isError ? (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(list.error)}`}>
            {isPermissionError(list.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(list.error, 'Failed to load subjects.')}
            </span>
          </div>
        ) : list.data && rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-muted">
            <Users className="mx-auto mb-2 h-6 w-6 opacity-50" />
            No subjects match the filters.
          </div>
        ) : list.data && rows.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-3 py-2" title="The subject's number in the IVRS/IWRS system. Blank until the vendor issues one.">
                      Subject ID
                    </th>
                    <th className="px-3 py-2" title="DreamTrials' own automatic subject number.">
                      Subject Reference ID
                    </th>
                    <th className="px-3 py-2">Study</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Re-consent</th>
                    <th
                      className="px-3 py-2"
                      title={
                        studyIdParam
                          ? 'Open compliance queries scoped to this subject on the filtered study.'
                          : 'Filter to a single study to see per-subject open query counts.'
                      }
                    >
                      Open queries
                    </th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.subject_id}
                      className="cursor-pointer border-b border-surface-border last:border-0 hover:bg-surface-muted/40"
                      onClick={() => navigate(rowHref(r))}
                    >
                      {/* Subject ID first (client, 17 Oct 2026). An em dash, not a
                          gap, so a blank reads as "not issued yet" (older
                          subjects) rather than a load failure. */}
                      <td className="px-3 py-2 font-mono text-sm font-medium text-ink-primary">
                        {r.ivrf_number || <span className="text-ink-muted">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-secondary">{r.external_ref}</td>
                      <td className="px-3 py-2 text-xs text-ink-secondary">
                        {studyNameById(studyById, r.study_id)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`badge ${subjectStatusBadgeClass(r.status)}`}>
                          {humanSubjectStatus(r.status)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.re_consent_required ? (
                          <span className="badge badge-warning">Required</span>
                        ) : (
                          <span className="badge badge-success">Not required</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {(() => {
                          if (!studyIdParam) return <span className="text-ink-muted">—</span>;
                          if (openCountsQ.isLoading)
                            return <span className="text-ink-muted">…</span>;
                          const n = openCountsBySubject.get(r.subject_id) ?? 0;
                          return n === 0 ? (
                            <span className="text-ink-muted">0</span>
                          ) : (
                            <span
                              className="badge badge-warning"
                              title={`${n} open compliance query(ies)`}
                            >
                              {n}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {fmtDateTime(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer — same layout as StudyPortfolioPage */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-border px-4 py-3 text-xs text-ink-muted">
              <div className="flex items-center gap-3">
                {total !== null ? (
                  <span>
                    Showing {from}–{to} of <strong className="text-ink-primary">{total}</strong>{' '}
                    {total === 1 ? 'subject' : 'subjects'}
                  </span>
                ) : (
                  <span>
                    Page {page} · {rows.length} rows
                  </span>
                )}
                <div className="flex items-center gap-1.5">
                  <span>Rows per page:</span>
                  <select
                    className="input py-0.5 text-xs w-16"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-3 w-3" /> Prev
                </button>
                <button
                  type="button"
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40"
                  disabled={!hasMore}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* New subject modal */}
      {creating && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !create.isPending) closeModal();
          }}
        >
          <form onSubmit={handleSubmit} className="card w-full max-w-lg space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-ink-primary">New subject</h2>
              <button
                type="button"
                onClick={closeModal}
                disabled={create.isPending}
                className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div>
              <label className="label text-xs" htmlFor="new-study">
                Study *
              </label>
              {studiesQuery.isLoading ? (
                <div className="input flex items-center gap-2 text-xs text-ink-muted">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading studies…
                </div>
              ) : studiesQuery.isError ? (
                <div className={`text-xs ${errorToneClass(studiesQuery.error)}`}>
                  {errorText(studiesQuery.error, 'Failed to load studies.')}
                </div>
              ) : studyModalOptions.length === 0 ? (
                <div className="text-xs text-ink-muted">
                  No active studies — create a study first.
                </div>
              ) : (
                <SingleSelectSearch
                  id="new-study"
                  options={studyModalOptions}
                  value={draft.study_id || null}
                  onChange={(v) => setDraft({ ...draft, study_id: v ?? '' })}
                  placeholder="Search studies by name or protocol…"
                  disabled={create.isPending}
                />
              )}
            </div>

            {/*
              External Reference is server-minted (SUB-####) at creation
              time — no user input required (Phase 2 Defect 2.4).
              A gentle inline note reminds the coordinator of the format.
            */}
            <div className="rounded border border-surface-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-muted">
              A Subject Reference will be automatically assigned in the format
              <span className="mx-1 font-mono font-semibold text-ink-primary">SUB-####</span>
              when this subject is created.
            </div>

            <div>
              <label className="label text-xs" htmlFor="new-fn">
                Full name (optional, KMS-encrypted)
              </label>
              <input
                id="new-fn"
                className="input"
                value={draft.full_name}
                disabled={create.isPending}
                onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={closeModal}
                disabled={create.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={create.isPending || !draft.study_id.trim()}
              >
                {create.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Create
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

